import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNewSessionModal } from '../context/NewSessionModalContext';
import { getUnreadCounts } from '../api/client';

export default function Layout({ children }) {
  const { user, logout, profile } = useAuth();
  const { openNewSession } = useNewSessionModal();
  const navigate = useNavigate();
  const location = useLocation();
  const [loggingOut, setLoggingOut] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadTotal, setUnreadTotal] = useState(0);

  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);
  useEffect(() => {
    const handleResize = () => { if (window.innerWidth > 768) setSidebarOpen(false); };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!user) return;
    const fetchUnread = () => {
      getUnreadCounts().then(res => {
        const counts = res.data || {};
        setUnreadTotal(Object.values(counts).reduce((a, b) => a + b, 0));
      }).catch(() => {});
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 15000);
    return () => clearInterval(interval);
  }, [user]);

  const handleLogout = async () => {
    setLoggingOut(true);
    await logout();
    navigate('/login');
  };

  const showGroups = profile?.session_focus !== 'individual';

  const navItems = [
    {
      path: '/',
      label: 'Dashboard',
      icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="1" y="1" width="6" height="6" rx="1.5" /><rect x="9" y="1" width="6" height="6" rx="1.5" /><rect x="1" y="9" width="6" height="6" rx="1.5" /><rect x="9" y="9" width="6" height="6" rx="1.5" /></svg>,
    },
    {
      path: '/clients',
      label: 'Clients',
      icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="6" cy="5" r="2.5" /><path d="M1 13c0-2.8 2.2-5 5-5s5 2.2 5 5" /><circle cx="12" cy="5" r="2" /><path d="M12 9c1.7.3 3 1.8 3 3.5" /></svg>,
    },
    {
      path: '/messages',
      label: 'Messages',
      icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="1" y="2.5" width="14" height="9" rx="2" /><path d="M4.5 14v-2.5M4.5 6.5h7M4.5 9h4.5" /></svg>,
    },
    ...(showGroups ? [{
      path: '/groups',
      label: 'Groups',
      icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="8" cy="5" r="2.5" /><path d="M3 13c0-2.8 2.2-5 5-5s5 2.2 5 5" /><circle cx="2.5" cy="5" r="1.8" /><path d="M0 12c0-2 1.3-3.5 2.5-4" /><circle cx="13.5" cy="5" r="1.8" /><path d="M16 12c0-2-1.3-3.5-2.5-4" /></svg>,
    }] : []),
    {
      path: '/schedule',
      label: 'Schedule',
      icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="1" y="3" width="14" height="11" rx="2" /><path d="M5 1v4M11 1v4M1 7h14" /></svg>,
    },
    {
      path: '/marketing',
      label: 'Marketing',
      icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M8 1l1.8 3.6L14 5.4l-3 2.9.7 4.1L8 10.4l-3.7 1.9.7-4.1L2 5.4l4.2-.8L8 1z" /></svg>,
    },
    {
      path: '/session/new',
      label: 'New Session',
      icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="8" cy="8" r="6.5" /><line x1="8" y1="5" x2="8" y2="11" /><line x1="5" y1="8" x2="11" y2="8" /></svg>,
      accent: true,
    },
    {
      path: '/settings',
      label: 'Settings',
      icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="8" cy="8" r="2.5" /><path d="M8 1.5v1.2M8 13.3v1.2M1.5 8h1.2M13.3 8h1.2M3.2 3.2l.85.85M11.95 11.95l.85.85M3.2 12.8l.85-.85M11.95 4.05l.85-.85" /></svg>,
    },
  ];

  // Not real authorization (the backend enforces that) — just keeps the nav item
  // out of the way for every coach except the app's operator.
  if ((user?.email || '').toLowerCase() === 'stevo2012@gmail.com') {
    navItems.push({
      path: '/admin',
      label: 'Admin',
      icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M8 1l6 2.5v4c0 4-2.6 6.8-6 7.5-3.4-.7-6-3.5-6-7.5v-4L8 1z" /></svg>,
    });
  }

  const isActive = (path) => {
    if (path === '/clients') return location.pathname.startsWith('/clients');
    if (path === '/groups') return location.pathname.startsWith('/groups');
    if (path === '/pending-recordings') return location.pathname.startsWith('/pending-recordings') || location.pathname.startsWith('/import-recording');
    return location.pathname === path;
  };

  const initials = (user?.user_metadata?.full_name || user?.email || 'U')
    .split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', fontFamily: "'DM Sans', system-ui, sans-serif" }}>

      {/* Hamburger */}
      <button
        className="hamburger-btn"
        style={styles.hamburger}
        onClick={() => setSidebarOpen(!sidebarOpen)}
        aria-label="Toggle menu"
      >
        <span style={{ ...styles.hamburgerBar, transform: sidebarOpen ? 'rotate(45deg) translate(5px, 5px)' : 'none' }} />
        <span style={{ ...styles.hamburgerBar, opacity: sidebarOpen ? 0 : 1 }} />
        <span style={{ ...styles.hamburgerBar, transform: sidebarOpen ? 'rotate(-45deg) translate(5px, -5px)' : 'none' }} />
      </button>

      {sidebarOpen && <div style={styles.overlay} onClick={() => setSidebarOpen(false)} />}

      {/* Sidebar */}
      <aside className={`tc-sidebar${sidebarOpen ? ' open' : ''}`}>

        {/* Logo */}
        <div style={styles.sidebarHeader}>
          <img src="/favicon.svg" alt="" style={styles.logoMark} />
          <div>
            <div style={styles.logoText}>Transformation</div>
            <div style={styles.logoSub}>Companion</div>
          </div>
        </div>

        {/* Nav */}
        <nav style={styles.nav}>
          {navItems.map(item => {
            const active = isActive(item.path);
            return (
              <button
                key={item.path}
                id={`tour-${item.label.toLowerCase().replace(' ', '-')}`}
                className="tc-nav-btn"
                onClick={() => item.path === '/session/new' ? openNewSession() : navigate(item.path)}
                style={{
                  ...styles.navBtn,
                  ...(active ? styles.navBtnActive : {}),
                  ...(item.accent && !active ? styles.navBtnAccent : {}),
                }}
              >
                <span style={{
                  ...styles.navIcon,
                  color: active ? 'var(--gold-light)' : item.accent ? 'var(--gold)' : 'rgba(255,255,255,0.5)',
                }}>
                  {item.icon}
                </span>
                <span style={{ color: active ? 'var(--gold-light)' : item.accent ? 'var(--gold-light)' : 'rgba(255,255,255,0.6)' }}>
                  {item.label}
                </span>
                {item.path === '/messages' && unreadTotal > 0 && (
                  <span style={styles.navBadge}>{unreadTotal > 9 ? '9+' : unreadTotal}</span>
                )}
                {active && !(item.path === '/messages' && unreadTotal > 0) && <span style={styles.activeIndicator} />}
              </button>
            );
          })}
        </nav>

        {/* Footer */}
        <div style={styles.sidebarFooter}>
          <div style={styles.userCard}>
            <div style={styles.userAvatar}>{initials}</div>
            <div style={styles.userMeta}>
              <div style={styles.userName}>
                {user?.user_metadata?.full_name || 'Practitioner'}
              </div>
              <div style={styles.userEmail}>{user?.email}</div>
            </div>
          </div>
          <button
            className="tc-logout-btn"
            onClick={handleLogout}
            style={styles.logoutBtn}
            disabled={loggingOut}
          >
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M6 2H3a1 1 0 00-1 1v10a1 1 0 001 1h3M10 11l3-3-3-3M13 8H6" />
            </svg>
            {loggingOut ? 'Signing out...' : 'Sign out'}
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="tc-main">
        {children}
      </main>
    </div>
  );
}

const styles = {
  hamburger: { position: 'fixed', top: '12px', left: '12px', zIndex: 400, background: 'var(--forest)', border: 'none', borderRadius: '8px', width: '44px', height: '44px', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '5px', cursor: 'pointer', padding: '12px' },
  hamburgerBar: { display: 'block', width: '20px', height: '2px', background: 'white', borderRadius: '2px', transition: 'all 0.2s' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 299 },
  sidebarHeader: { padding: '24px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', gap: '12px' },
  logoMark: { width: '36px', height: '36px', borderRadius: '10px', flexShrink: 0, display: 'block', boxShadow: '0 2px 8px rgba(0,0,0,0.2)' },
  logoText: { fontFamily: "'Lora', Georgia, serif", fontSize: '14px', fontWeight: '500', color: 'rgba(255,255,255,0.9)', lineHeight: 1.2 },
  logoSub: { fontFamily: "'Lora', Georgia, serif", fontSize: '14px', fontWeight: '400', fontStyle: 'italic', color: 'var(--gold-light)', lineHeight: 1.2 },
  nav: { flex: 1, padding: '12px 10px', display: 'flex', flexDirection: 'column', gap: '2px' },
  navBtn: { width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '10px 12px', borderRadius: '8px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", fontSize: '13.5px', display: 'flex', alignItems: 'center', gap: '10px', position: 'relative', minHeight: '42px' },
  navBtnActive: { background: 'rgba(196,149,106,0.15)', },
  navBtnAccent: { background: 'rgba(196,149,106,0.08)' },
  navIcon: { display: 'flex', alignItems: 'center', flexShrink: 0 },
  activeIndicator: { position: 'absolute', right: '10px', width: '5px', height: '5px', borderRadius: '50%', background: 'var(--gold)' },
  navBadge: { marginLeft: 'auto', background: 'var(--red)', color: 'white', borderRadius: '20px', fontSize: '10.5px', fontWeight: '700', padding: '1px 6px', minWidth: '16px', textAlign: 'center', lineHeight: '15px' },
  sidebarFooter: { padding: '10px', borderTop: '1px solid rgba(255,255,255,0.06)' },
  userCard: { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 10px', marginBottom: '4px', borderRadius: '8px' },
  userAvatar: { width: '32px', height: '32px', borderRadius: '50%', background: 'var(--forest-light)', border: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '600', color: 'var(--gold-light)', flexShrink: 0 },
  userMeta: { flex: 1, minWidth: 0 },
  userName: { fontSize: '12.5px', color: 'rgba(255,255,255,0.8)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: '500' },
  userEmail: { fontSize: '11px', color: 'rgba(255,255,255,0.35)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: '1px' },
  logoutBtn: { width: '100%', padding: '8px 12px', borderRadius: '8px', border: 'none', background: 'none', color: 'rgba(255,255,255,0.3)', fontFamily: "'DM Sans', sans-serif", fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', minHeight: '36px' },
};